import React,{useState} from 'react';
import {motion,AnimatePresence} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from './common/SafeIcon';
import './App.css';

// Destructure icons for easy usage 
const {FiArrowRight,FiCheckCircle,FiTarget,FiUser,FiSettings,FiBook,FiAlertCircle,FiExternalLink,FiCpu,FiZap,FiTrendingUp,FiClock,FiActivity,FiStar,FiAward,FiPhone,FiShield,FiPocket}=FiIcons;

const questions=[
  {id: 'current_ai_use',question: 'How would you describe your current use of AI in your practice?',options: [ "I'm curious but overwhelmed","I've bought tools I never use","I use AI inconsistently","I've implemented some workflows","I want full automation" ]},
  {id: 'organization_level',question: 'How organized are your current systems?',options: [ "Very organized","Organized but clunky","Total chaos","Organized in my head","Depends on the day" ]},
  {id: 'main_goal',question: 'What is your main goal with AI?',options: [ "Save time","Reduce admin","Improve client care","Support my group practice","Scale beyond one to one","I want someone to build it for me" ]},
  {id: 'learning_style',question: 'How do you prefer to learn?',options: [ "Quick templates","Step by step","Done for me","A mix of all three" ]},
  {id: 'biggest_obstacle',question: 'What has been the biggest obstacle so far?',options: [ "Overwhelm","Fear of choosing wrong","No time","Tech confusion","Buying tools I don't use","Not knowing where to start" ]}
];

const pathways={
  1: {name: "Clinical AI Club",description: "For therapists who are overwhelmed, inconsistent, or need structured help implementing simple workflows.",link: "https://www.clinicalaiclub.com",color: "from-teal-500 to-emerald-400"},
  2: {name: "Thera AI Hub",description: "For therapists who want a library of GPTs, templates, and tools to use immediately and on their own pace.",link: "https://kymtolson.kartra.com/page/AI-Tools-for-Therapists",color: "from-cyan-500 to-blue-400"},
  3: {name: "Club + Hub Bundle",description: "For therapists who want both: ongoing support plus the full library of tools.",link: "https://kymtolson.kartra.com/page/AI-Tools-for-Therapists",color: "from-indigo-500 to-purple-400"},
  4: {name: "Build a Custom GPT",description: "For therapists who want a specific automation or tool built for them.",link: "https://calendly.com/kymtolson/build-an-ai-gpt-with-kym",color: "from-orange-500 to-red-400"},
  5: {name: "60 Minute Paid Consult",description: "For therapists who need personalized guidance, clarity, or help choosing the right AI direction.",link: "https://calendly.com/kymtolson/60min",color: "from-blue-600 to-indigo-500"},
  6: {name: "60 Day AI Powered Practice Intensive",description: "For therapists who want private coaching and a full AI automation buildout over 60 days.",link: "https://clinicalaiclub.com/the-ai-powered-private-practice-intensive/",color: "from-teal-700 to-blue-800"},
  7: {name: "Discovery Call",description: "For therapists who are unsure which path fits them best.",link: "https://calendly.com/kymtolson/lets-chat-collaboration-meet-up-clone",color: "from-slate-600 to-slate-400"}
};

const LandingPage=({onStart})=> (
  <motion.div initial={{opacity: 0}} animate={{opacity: 1}} exit={{opacity: 0,y: -20}} className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden" >
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      <div className="absolute top-[-10%] left-[-10%] w-96 h-96 bg-teal-200 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-blob"></div>
      <div className="absolute top-[-10%] right-[-10%] w-96 h-96 bg-cyan-200 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-blob animation-delay-2000"></div>
      <div className="absolute -bottom-32 left-20 w-96 h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-blob animation-delay-4000"></div>
    </div>
    <div className="max-w-4xl w-full z-10">
      <div className="bg-white/40 backdrop-blur-xl rounded-3xl shadow-xl border border-white/60 p-8 md:p-12 text-center text-slate-800 overflow-hidden relative">
        <div className="absolute inset-0 bg-grid-white/[0.05] bg-[length:40px_40px] pointer-events-none"></div>
        <motion.div initial={{scale: 0.9,opacity: 0,y: 20}} animate={{scale: 1,opacity: 1,y: 0}} transition={{delay: 0.2}} className="relative" >
          <div className="inline-flex items-center justify-center p-3 mb-6 rounded-2xl bg-gradient-to-tr from-teal-500 to-cyan-400 shadow-lg border border-white/20">
            <SafeIcon icon={FiCpu} className="text-4xl text-white" />
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-teal-800 to-blue-800">
            Which AI Tool Is Right for You
          </h1>
          <p className="text-xl text-slate-600 mb-10 max-w-2xl mx-auto leading-relaxed font-medium">
            Discover the perfect AI strategy for your therapy practice. 
            Get a personalized roadmap in under 2 minutes.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12 text-left">
            <FeatureCard icon={FiTarget} title="Tailored Roadmap" desc="Get a custom path based on your specific needs and goals." />
            <FeatureCard icon={FiZap} title="Instant Clarity" desc="Stop the overwhelm and know exactly what to do next." />
            <FeatureCard icon={FiTrendingUp} title="Practice Growth" desc="Unlock tools that help you scale and save time." />
          </div>
          <motion.button onClick={onStart} whileHover={{scale: 1.05,boxShadow: "0 20px 25px -5px rgba(13, 148, 136, 0.2), 0 10px 10px -5px rgba(13, 148, 136, 0.1)"}} whileTap={{scale: 0.95}} className="group relative inline-flex items-center gap-3 px-10 py-5 bg-teal-600 text-white rounded-2xl text-xl font-bold shadow-xl transition-all hover:bg-teal-700" >
            Find Your AI Tool
            <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1 transition-transform" />
          </motion.button>
        </motion.div>
      </div>
    </div>
  </motion.div>
);

const FeatureCard=({icon,title,desc})=> (
  <div className="bg-white/60 rounded-2xl p-5 border border-white/50 backdrop-blur-md hover:bg-white/80 transition-colors duration-300 shadow-sm hover:shadow-md">
    <SafeIcon icon={icon} className="text-3xl text-teal-600 mb-3" />
    <h3 className="font-bold text-lg mb-2 text-slate-800">{title}</h3>
    <p className="text-sm text-slate-600 leading-relaxed">{desc}</p>
  </div>
);

function App() {
  const [started,setStarted]=useState(false);
  const [currentQuestion,setCurrentQuestion]=useState(0);
  const [answers,setAnswers]=useState({});
  const [showResults,setShowResults]=useState(false);
  const [recommendation,setRecommendation]=useState(null);

  const handleAnswer=(answer)=> {
    const questionId=questions[currentQuestion].id;
    const newAnswers={...answers,[questionId]: answer};
    setAnswers(newAnswers);
    if (currentQuestion < questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    } else {
      const pathway=determinePathway(newAnswers);
      setTimeout(()=> {
        setRecommendation(pathway);
        setShowResults(true);
      },500);
    }
  };

  const determinePathway=(answers)=> {
    const {current_ai_use,organization_level,learning_style,main_goal,biggest_obstacle}=answers;
    const pathwayScores={1:0, 2:0, 3:0, 4:0, 5:0, 6:0, 7:0};

    // Refined Scoring Logic
    if (["I'm curious but overwhelmed","I've bought tools I never use"].includes(current_ai_use)) pathwayScores[1] += 3;
    if (learning_style?.toLowerCase()==="quick templates" && main_goal?.toLowerCase()==="save time") pathwayScores[2] += 8;
    if (learning_style?.toLowerCase()==="a mix of all three") pathwayScores[3] += 4;
    if (main_goal?.toLowerCase()==="i want someone to build it for me") pathwayScores[4] += 5;
    if (biggest_obstacle?.toLowerCase()==="tech confusion") pathwayScores[5] += 3;
    
    // Pathway 6 (Intensive) only wins if they explicitly want full automation AND scale
    if (current_ai_use?.toLowerCase()==="i want full automation") pathwayScores[6] += 5;
    if (main_goal?.toLowerCase()==="scale beyond one to one") pathwayScores[6] += 3;
    
    pathwayScores[7] = 1;

    const sortedPathways=Object.entries(pathwayScores)
      .sort((a,b)=> b[1] - a[1])
      .map(([pathwayId,score])=> ({id: parseInt(pathwayId),score}));

    const topPathway=sortedPathways[0];
    const secondPathway=sortedPathways[1];

    const generateRecommendation=(pathwayId)=> {
      const base={
        pathway: pathwayId,
        summary: `Based on your goal to ${main_goal?.toLowerCase()}, this path provides the best balance of speed and results.`,
        reason: "This recommendation is tailored to your current workflow organization and learning style.",
        action: "Take the first step today by reviewing the resources available in this program."
      };
      
      if(pathwayId === 1) {
        base.summary = "You're looking for structured support to overcome AI overwhelm.";
        base.reason = "The Clinical AI Club offers the community and step-by-step guidance you need.";
        base.action = "Join our next live workshop to see how simple these tools can be.";
      }
      if(pathwayId === 6) {
        base.summary = "You're ready for a complete practice transformation and full automation.";
        base.reason = "The 60 Day Intensive is the ultimate 'Done-With-You' experience for maximum results.";
        base.action = "Schedule your interview for the Intensive to see if we're a good fit.";
      }
      return base;
    };

    const rec = generateRecommendation(topPathway.id);
    
    // Set second recommendation if it's not the same as top
    rec.secondRecommendation = secondPathway.id !== topPathway.id ? {
      id: secondPathway.id,
      ...pathways[secondPathway.id]
    } : null;

    // ALWAYS include the 60-Day Intensive (Pathway 6) as the "Premium/Scale" option
    // UNLESS it's already the primary or secondary recommendation
    if (topPathway.id !== 6 && secondPathway.id !== 6) {
      rec.featuredIntensive = {
        id: 6,
        ...pathways[6]
      };
    }

    return rec;
  };

  const resetAssessment=()=> {
    setStarted(false);
    setCurrentQuestion(0);
    setAnswers({});
    setShowResults(false);
    setRecommendation(null);
  };

  return (
    <div className="app-container min-h-screen font-sans">
      <AnimatePresence mode="wait">
        {!started ? (
          <LandingPage key="landing" onStart={()=> setStarted(true)} />
        ) : !showResults ? (
          <motion.div key="assessment" initial={{opacity: 0}} animate={{opacity: 1}} exit={{opacity: 0}} className="min-h-screen flex items-center justify-center py-12 px-4" >
            <div className="max-w-2xl w-full">
              <div className="bg-white/90 backdrop-blur-md rounded-2xl shadow-xl p-8 border border-white/60">
                <div className="mb-6">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-sm font-medium text-slate-500">Question {currentQuestion + 1} of {questions.length}</span>
                    <div className="flex space-x-1">
                      {questions.map((_,index)=> (
                        <div key={index} className={`w-2.5 h-2.5 rounded-full transition-colors duration-300 ${index <=currentQuestion ? 'bg-teal-600' : 'bg-slate-200'}`} />
                      ))}
                    </div>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2 overflow-hidden">
                    <div className="bg-teal-600 h-2 rounded-full transition-all duration-500 ease-out" style={{width: `${((currentQuestion + 1) / questions.length) * 100}%`}} />
                  </div>
                </div>

                <AnimatePresence mode="wait">
                  <motion.div key={currentQuestion} initial={{opacity: 0,x: 20}} animate={{opacity: 1,x: 0}} exit={{opacity: 0,x: -20}} transition={{duration: 0.3}} >
                    <h2 className="text-2xl font-bold text-slate-900 mb-8 leading-tight">{questions[currentQuestion].question}</h2>
                    <div className="space-y-3">
                      {questions[currentQuestion].options.map((option,index)=> (
                        <motion.button key={index} onClick={()=> handleAnswer(option)} className="w-full p-5 text-left bg-white hover:bg-teal-50 border-2 border-transparent hover:border-teal-200 rounded-xl transition-all duration-200 flex items-center justify-between group shadow-sm hover:shadow-md" whileHover={{scale: 1.01}} whileTap={{scale: 0.99}} >
                          <span className="text-lg text-slate-700 group-hover:text-teal-900 font-medium">{option}</span>
                          <SafeIcon icon={FiArrowRight} className="text-slate-300 group-hover:text-teal-600 transition-all duration-300" />
                        </motion.button>
                      ))}
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </motion.div>
        ) : (
          <motion.div key="results" initial={{opacity: 0}} animate={{opacity: 1}} exit={{opacity: 0}} className="min-h-screen flex items-center justify-center py-12 px-4" >
            {recommendation && (
              <div className="max-w-4xl w-full bg-white rounded-3xl shadow-2xl overflow-hidden">
                <div className={`bg-gradient-to-r ${pathways[recommendation.pathway].color} p-10 text-white text-center relative`}>
                  <div className="absolute top-4 right-4 bg-white/20 backdrop-blur-md px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">Top Match</div>
                  <SafeIcon icon={FiStar} className="text-6xl mx-auto mb-4 text-white/90" />
                  <h1 className="text-3xl font-bold mb-2">Your Personalized Path</h1>
                  <h2 className="text-4xl md:text-5xl font-extrabold mb-4">{pathways[recommendation.pathway].name}</h2>
                  <p className="text-xl opacity-90 max-w-2xl mx-auto">{pathways[recommendation.pathway].description}</p>
                </div>

                <div className="p-8 md:p-12 bg-white">
                  <div className="grid md:grid-cols-2 gap-8 mb-10">
                    <div className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
                      <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center">
                        <SafeIcon icon={FiUser} className="mr-2 text-teal-600" /> Analysis
                      </h3>
                      <p className="text-slate-600 leading-relaxed">{recommendation.summary}</p>
                    </div>
                    <div className="bg-teal-50 rounded-2xl p-6 border border-teal-100">
                      <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center">
                        <SafeIcon icon={FiTarget} className="mr-2 text-teal-600" /> Next Step
                      </h3>
                      <p className="text-slate-600 leading-relaxed font-medium">{recommendation.action}</p>
                    </div>
                  </div>

                  <div className="flex justify-center mb-16">
                    <motion.a href={pathways[recommendation.pathway].link} target="_blank" rel="noopener noreferrer" className={`w-full max-w-md bg-gradient-to-r ${pathways[recommendation.pathway].color} text-white px-8 py-5 rounded-2xl font-bold text-xl text-center hover:shadow-xl transition-all flex items-center justify-center`} whileHover={{scale: 1.02}} whileTap={{scale: 0.98}} >
                      Get Started with {pathways[recommendation.pathway].name.split(' ')[0]}
                      <SafeIcon icon={FiExternalLink} className="ml-2" />
                    </motion.a>
                  </div>

                  {/* Alternatives Section */}
                  <div className="border-t border-slate-100 pt-12">
                    <h3 className="text-2xl font-bold text-slate-900 mb-8 text-center">Additional Options for Your Practice</h3>
                    <div className="grid md:grid-cols-2 gap-6">
                      {/* Runner Up Recommendation */}
                      {recommendation.secondRecommendation && (
                        <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200 flex flex-col hover:border-teal-300 transition-colors">
                          <div className="flex items-center justify-between mb-4">
                            <span className="bg-slate-200 text-slate-600 px-3 py-1 rounded-full text-xs font-bold uppercase">Alternative Match</span>
                            <SafeIcon icon={FiAward} className="text-slate-400" />
                          </div>
                          <h4 className="text-xl font-bold text-slate-800 mb-2">{recommendation.secondRecommendation.name}</h4>
                          <p className="text-slate-600 text-sm mb-6 flex-grow">{recommendation.secondRecommendation.description}</p>
                          <a href={recommendation.secondRecommendation.link} target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-700 font-bold flex items-center gap-2 group" >
                            Explore This Path <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1 transition-transform" />
                          </a>
                        </div>
                      )}

                      {/* ALWAYS FEATURED (as long as not top/second): 60-Day Intensive */}
                      {recommendation.featuredIntensive && (
                        <div className="bg-teal-900 rounded-2xl p-6 border border-teal-800 flex flex-col text-white shadow-lg transform hover:-translate-y-1 transition-transform">
                          <div className="flex items-center justify-between mb-4">
                            <span className="bg-teal-600 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wide">Premium Build-Out</span>
                            <SafeIcon icon={FiShield} className="text-teal-400" />
                          </div>
                          <h4 className="text-xl font-bold mb-2">60 Day AI Powered Practice Intensive</h4>
                          <p className="text-teal-100/80 text-sm mb-6 flex-grow">A high-level private coaching experience where we build your full AI automation systems with you over 60 days. Perfect for scaling.</p>
                          <a href={recommendation.featuredIntensive.link} target="_blank" rel="noopener noreferrer" className="bg-white text-teal-900 py-3 rounded-xl font-bold text-center hover:bg-teal-50 transition-colors flex items-center justify-center gap-2" >
                            View Intensive Details <SafeIcon icon={FiExternalLink} className="text-sm" />
                          </a>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-12 pt-8 border-t border-slate-100 text-center">
                    <p className="text-slate-500 text-sm mb-4">Not sure? Let's talk about which path fits your specific practice needs.</p>
                    <motion.a href="https://calendly.com/kymtolson/lets-chat-collaboration-meet-up-clone" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-8 py-4 bg-teal-50 text-teal-700 rounded-2xl font-bold hover:bg-teal-100 transition-colors" whileHover={{scale: 1.02}} whileTap={{scale: 0.98}} >
                      <SafeIcon icon={FiPhone} /> Book a Free Discovery Call
                    </motion.a>
                    <div className="mt-8">
                      <button onClick={resetAssessment} className="text-slate-400 hover:text-slate-600 text-sm font-medium" >Retake Assessment</button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default App;